import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion, useMotionValue, animate as fmAnimate } from "framer-motion";
import { Leaf, Wallet, TreeDeciduous, Droplet, Package, Flame, Trophy, TrendingUp, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LineChart, Line, Tooltip as RTooltip } from "recharts";
import SustainabilityScore from "@/components/SustainabilityScore";
import GreenCoinsBalance from "@/components/GreenCoinsBalance";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const Counter = ({ to, className, suffix = "", decimals = 0 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const mv = { v: 0 };
    const c = fmAnimate(0, to, {
      duration: 1.2,
      onUpdate: (v) => setVal(v),
      ease: "easeOut",
    });
    return () => c.stop();
  }, [to]);
  return <span className={className}>{decimals ? val.toFixed(decimals) : Math.round(val)}{suffix}</span>;
};

const StatCard = ({ icon, label, value, sub, testId, tone = "emerald" }) => {
  const tones = {
    emerald: "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30",
    amber: "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30",
    blue: "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30",
    slate: "text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800",
  };
  return (
    <div className="card-premium p-6" data-testid={testId}>
      <div className={`inline-grid place-items-center h-10 w-10 rounded-full ${tones[tone]}`}>{icon}</div>
      <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mono text-3xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
};

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/dashboard").then((r) => setD(r.data));
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/dashboard" replace />;
  if (!d) return <div className="max-w-7xl mx-auto p-8">Loading dashboard...</div>;

  const { stats, recent_purchases, weekly_progress, monthly_progress, badges_all } = d;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-6 justify-between mb-10">
        <div className="flex items-center gap-5">
          <img src={user.avatar} alt="" className="h-16 w-16 rounded-full bg-muted" />
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Welcome back</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{user.name}</h1>
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-3 py-1 text-xs font-semibold">
              <Trophy className="h-3.5 w-3.5" /> {stats.badge.name}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <GreenCoinsBalance balance={stats.available_points} />
          <SustainabilityScore score={Math.round(stats.avg_sustainability_score || 0)} size={90} stroke={7} />
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Your avg score</div>
            <div className="text-sm text-muted-foreground max-w-[10rem]">Based on {stats.purchase_count} purchases</div>
          </div>
        </div>
      </div>

      {/* Stats Bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        <StatCard icon={<Leaf className="h-5 w-5" />} label="Green Points" value={<Counter to={stats.available_points} />} sub={`${stats.total_points} lifetime`} testId="stat-points" tone="emerald" />
        <StatCard icon={<Wallet className="h-5 w-5" />} label="Cashback Wallet" value={<><span className="text-lg font-normal text-muted-foreground">₹</span><Counter to={stats.cashback_wallet} /></>} testId="stat-cashback" tone="amber" />
        <StatCard icon={<Flame className="h-5 w-5" />} label="Carbon Saved" value={<><Counter to={stats.carbon_saved_kg} decimals={1} /><span className="text-lg font-normal text-muted-foreground"> kg</span></>} testId="stat-carbon" tone="slate" />
        <StatCard icon={<TreeDeciduous className="h-5 w-5" />} label="Trees Equivalent" value={<Counter to={stats.trees_equivalent} decimals={1} />} testId="stat-trees" tone="emerald" />
        <StatCard icon={<Package className="h-5 w-5" />} label="Plastic Avoided" value={<><Counter to={stats.plastic_avoided_g} /><span className="text-lg font-normal text-muted-foreground"> g</span></>} testId="stat-plastic" tone="slate" />
        <StatCard icon={<Droplet className="h-5 w-5" />} label="Water Saved" value={<><Counter to={stats.water_saved_l} /><span className="text-lg font-normal text-muted-foreground"> L</span></>} testId="stat-water" tone="blue" />
        <StatCard icon={<TrendingUp className="h-5 w-5" />} label="Current Streak" value={<><Counter to={stats.streak_days} /><span className="text-lg font-normal text-muted-foreground"> days</span></>} testId="stat-streak" tone="amber" />
        <StatCard icon={<Trophy className="h-5 w-5" />} label="Purchases" value={<Counter to={stats.purchase_count} />} testId="stat-purchases" tone="slate" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5 mt-8">
        <div className="card-premium p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">Weekly Progress</h3>
            <span className="text-xs text-muted-foreground">Last 7 days</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekly_progress}>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <RTooltip />
                <Bar dataKey="points" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-premium p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">Monthly Trend</h3>
            <span className="text-xs text-muted-foreground">Last 6 months</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthly_progress}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <RTooltip />
                <Line type="monotone" dataKey="points" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="mt-10">
        <h3 className="text-xl font-bold mb-4">Your Journey</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="badges-timeline">
          {badges_all.map((b) => {
            const unlocked = stats.available_points >= b.min_points;
            return (
              <div
                key={b.tier}
                className={`card-premium p-5 text-center transition-all ${unlocked ? "" : "opacity-40 grayscale"}`}
                data-testid={`badge-${b.tier}`}
              >
                <div
                  className="h-14 w-14 mx-auto rounded-full grid place-items-center shadow-lg"
                  style={{ backgroundColor: b.color + "20", boxShadow: unlocked ? `0 0 30px ${b.color}40` : "none" }}
                >
                  <Trophy className="h-7 w-7" style={{ color: b.color }} />
                </div>
                <div className="text-sm font-bold mt-3">{b.name}</div>
                <div className="mono text-[10px] text-muted-foreground mt-1">{b.min_points}+ pts</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pending rewards banner */}
      {(stats.pending_points > 0 || stats.pending_cashback > 0) && (
        <div className="mt-6 rounded-3xl border border-amber-200/70 bg-amber-50/70 dark:bg-amber-900/20 dark:border-amber-800/50 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="pending-banner">
          <div className="inline-grid place-items-center h-11 w-11 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 shrink-0">
            <Clock className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
              {stats.pending_points} coins &amp; ₹{stats.pending_cashback} cashback pending approval
            </div>
            <div className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
              Rewards are credited to your wallet once the merchant confirms your purchase and the return / exchange window closes.
            </div>
          </div>
        </div>
      )}

      {/* Recent purchases */}
      {recent_purchases.length > 0 && (
        <div className="mt-10">
          <h3 className="text-xl font-bold mb-4">Recent Purchases</h3>
          <div className="grid gap-3" data-testid="recent-purchases">
            {recent_purchases.map((p) => {
              const status = p.status || "pending";
              const statusStyles = {
                pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
                confirmed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
                rejected: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
              }[status];
              const statusLabel = {
                pending: "Pending approval",
                confirmed: "Credited",
                rejected: "Rejected",
              }[status];
              return (
                <div key={p.id} className="card-premium p-4 flex items-center gap-4" data-testid={`purchase-${p.id}`}>
                  <img src={p.product_image} alt="" className="h-14 w-14 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.product_name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <span>{new Date(p.created_at).toLocaleDateString()} · {p.sustainability_score}/100 score</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusStyles}`}>
                        {status === "pending" && <Clock className="h-2.5 w-2.5" />}
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                  <div className={`text-right shrink-0 ${status !== "confirmed" ? "opacity-60" : ""}`}>
                    <div className="mono text-emerald-600 font-bold">+{p.green_points} pts</div>
                    <div className="text-xs text-muted-foreground">₹{p.cashback} back</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
